import { Core, UI } from '@pdftron/webviewer';
import { RefObject, useCallback, useEffect, useMemo } from 'react';

import { useWebViewerDocumentState } from 'components/organisms/reusableWebviewer';
import { WebViewerEventHandlers } from 'components/organisms/reusableWebviewer/types';
import {
  useWebViewer as useReusableWebviewer,
  useWebviewerSettings,
} from 'components/organisms/reusableWebviewer/useWebviewer';
import { useApplicationErrorHandling } from 'hooks/useApplicationErrorHandling';
import { useAsyncCreatedResource } from 'hooks/useAsyncCreatedResource';
import { ApplicationError } from 'types/errorHandling';
import { NEW_INDEXING_BARCODE_DPI } from 'utils/localFlags';
import { sleep } from 'utils/sleep';

import { ID } from './types';

export const UseWebviewerPageIDKey = '$$WorkOptimaPageID$$';

const RASTERIZE_RESOLUTION =
  (NEW_INDEXING_BARCODE_DPI.value !== -1
    ? NEW_INDEXING_BARCODE_DPI.value
    : undefined) ??
  (import.meta.env.REACT_APP_INDEXING_RASTERIZE_RESOLUTION &&
    parseInt(import.meta.env.REACT_APP_INDEXING_RASTERIZE_RESOLUTION)) ??
  240;

NEW_INDEXING_BARCODE_DPI.addEventListener('changed', () => {
  window.location.reload();
});

console.debug(`rasterizing PDFs at ${RASTERIZE_RESOLUTION} DPI`);

export type PageImage = {
  data: string;
  aspectRatio: number;
};

const pdfDrawCleanup = (pdfdraw?: Core.PDFNet.PDFDraw) => {
  pdfdraw?.destroy();
};

export function useWebViewer(
  webViewerElementRef: RefObject<HTMLDivElement>,
  allowAddReorder: boolean = true,
  eventHandlers: WebViewerEventHandlers
) {
  const webViewerInstance = useReusableWebviewer(webViewerElementRef, true);

  useWebviewerSettings(
    webViewerInstance,
    false,
    useMemo(() => ['FilePicker'], []),
    useMemo(
      () => [
        'viewControlsDivider1',
        'rotateHeader',
        'rotateClockwiseButton',
        'rotateCounterClockwiseButton',
        'viewControlsDivider2',
        'layoutHeader',
        'singleLayoutButton',
        'doubleLayoutButton',
        'coverLayoutButton',
      ],
      []
    )
  );

  useWebviewerSettings(
    webViewerInstance,
    allowAddReorder,
    useMemo(
      (): Array<keyof typeof UI.Feature> => [
        'ThumbnailMerging',
        'ThumbnailReordering',
      ],
      []
    ),
    useMemo(
      () => ['insertPage', 'replacePage', 'deletePage', 'thumbDelete'],
      []
    )
  );

  useEffect(() => {
    if (!webViewerInstance) return;

    webViewerInstance.UI.enableFeatures(['MultiTab']);

    return () => {
      webViewerInstance.UI.TabManager.addTab('', { setActive: true });
      webViewerInstance.UI.disableFeatures(['MultiTab']);
    };
  }, [webViewerInstance]);

  const {
    pageCount,
    viewerDocument,
    setCurrentPageNumber,
    resetViewerDocument,
  } = useWebViewerDocumentState(webViewerInstance, eventHandlers);

  const resetViewer = useCallback(async () => {
    await resetViewerDocument();

    const tabManager = webViewerInstance?.UI.TabManager;

    if (!tabManager) return;

    tabManager.disableDeleteTabWarning();

    tabManager.getAllTabs().forEach((tab) => {
      // @ts-expect-error - bad typing on getAllTabs
      tabManager.deleteTab(tab.id);
    });
  }, [resetViewerDocument, webViewerInstance?.UI.TabManager]);

  const showThumbnailViewer = useCallback(async () => {
    // TODO: don't use delays to solve this, actually figure out what's causing thumbnails to not display sometimes
    await sleep(250);
    webViewerInstance?.UI.openElements(['tabPanel']);

    await sleep(250);
    webViewerInstance?.UI.setActiveLeftPanel('thumbnailsPanel');
  }, [webViewerInstance]);
  const hideThumbnailViewer = useCallback(() => {
    webViewerInstance?.UI.closeElements(['tabPanel']);
  }, [webViewerInstance?.UI]);
  useEffect(
    () => () => {
      hideThumbnailViewer();
    },
    [hideThumbnailViewer, showThumbnailViewer, webViewerInstance]
  );
  useEffect(() => {
    pageCount > 0 ? showThumbnailViewer() : hideThumbnailViewer();
  }, [hideThumbnailViewer, pageCount, showThumbnailViewer]);

  useEffect(() => {
    if (!webViewerInstance) return;
    if (!viewerDocument) return;

    const manager =
      webViewerInstance.Core.documentViewer.getDisplayModeManager();

    const displayModeConstructor = manager.isVirtualDisplayEnabled()
      ? webViewerInstance.Core.VirtualDisplayMode
      : webViewerInstance.Core.DisplayMode;

    // @ts-expect-error - untyped return value
    const initialMode = manager.getDisplayMode().getMode() as Core.DisplayModes;

    manager.setDisplayMode(
      new displayModeConstructor(
        webViewerInstance.Core.documentViewer,
        webViewerInstance.Core.DisplayModes.Single,
        true
      )
    );

    return () => {
      manager.setDisplayMode(
        new displayModeConstructor(
          webViewerInstance.Core.documentViewer,
          initialMode,
          true
        )
      );
    };
  }, [viewerDocument, webViewerInstance]);

  const handleApplicationError = useApplicationErrorHandling();
  /*
    This will only fire when the user uses drag/drop or the built-in "Browse Files"
    button to open a file, and the file is corrupted. Bad files from other sources
    are blocked in the addDocument function.
  */
  useEffect(() => {
    if (!webViewerInstance) return;

    const errHandler = (error: Event): void => {
      const tabmanager = webViewerInstance.UI.TabManager;

      const tab = tabmanager.getActiveTab();

      webViewerInstance.UI.closeElements(['errorModal', 'progressModal']);
      tabmanager.deleteTab(tab.id);

      handleApplicationError(
        new ApplicationError(
          'Failed to load file. Please use another PDF-viewing application to check the file for corruption.',
          'webviewer reported LOAD_ERROR',
          {
            details: {
              error,
              tab,
            },
          }
        )
      );
    };

    webViewerInstance.UI.addEventListener(
      webViewerInstance.UI.Events.LOAD_ERROR,
      errHandler
    );

    return () => {
      webViewerInstance.UI.removeEventListener(
        webViewerInstance.UI.Events.LOAD_ERROR,
        errHandler
      );
    };
  }, [handleApplicationError, webViewerInstance]);

  const addDocument = useCallback(
    async (data: File) => {
      if (!webViewerInstance) return;

      const tabmanager = webViewerInstance.UI.TabManager;

      if (tabmanager.getAllTabs().length !== 0) {
        if (!allowAddReorder)
          throw new ApplicationError(
            'Document merging is disabled right now.',
            'allowAddReorder was false'
          );

        const tab = tabmanager.getActiveTab();

        const tabSrc = tab.src;

        if (tabSrc instanceof webViewerInstance.Core.Document) {
          await tabSrc.mergeDocument(data);
        } else {
          // this should never happen, but just in case...
          throw new ApplicationError(
            'Failed to merge files due to unexpected type',
            'tab source was not document',
            {
              details: { tabSrc },
            }
          );
        }
      } else {
        const newDoc = await webViewerInstance.Core.createDocument(data, {
          loadAsPDF: true,
        });

        await tabmanager.addTab(newDoc, { setActive: true });
      }
    },
    [allowAddReorder, webViewerInstance]
  );

  /**
   * An async iterator that returns page number and ID for all pages with IDs.
   *
   * ⚠️ Iterators are not safe to retain over long durations, as they depend on
   * a page count that will not update after instantiation.
   */
  const getAllPageData = useCallback(
    async function* () {
      const doc = await webViewerInstance?.Core.documentViewer
        .getDocument()
        .getPDFDoc();
      if (!doc) return;

      const itr = await doc.getPageIterator(1);
      let pgnum = 1;

      let current = await itr.current();
      while (current) {
        try {
          const id = (await current
            .getSDFObj()
            .then((rd) => rd.findObj(UseWebviewerPageIDKey))
            .then((idobj) => idobj.getAsPDFText())) as ID;

          yield { page: pgnum, id };
        } catch {}

        // This should be itr.hasNext, but itr will return an extra page that can't be reliably filtered out.
        if (pgnum++ < pageCount) {
          await itr.next();
          current = await itr.current();
        } else {
          break;
        }
      }
    },
    [pageCount, webViewerInstance?.Core.documentViewer]
  );

  const renderPage = useCallback(
    async (
      pageNum: number,
      height: number,
      abortSignal?: AbortSignal
    ): Promise<PageImage> => {
      if (!webViewerInstance)
        throw new ApplicationError(
          'Viewer not initialized',
          'viewer not initialized'
        );

      const viewer = webViewerInstance.Core.documentViewer;

      const doc = viewer.getDocument();

      const pageCanvas = await new Promise(
        (resolve: Core.Document.DrawCompleteCallback, reject) => {
          const loadId = doc.loadCanvas({
            pageNumber: pageNum,
            drawComplete: resolve,
            zoom: height / doc.getPageInfo(pageNum).height,
          });

          abortSignal?.addEventListener('abort', () => {
            // @ts-expect-error the typing of loadID is wrong
            doc.cancelLoadCanvas(loadId);

            reject('abort at loadCanvas');
          });
        }
      )
        .then((value) => {
          if (value instanceof HTMLCanvasElement) return value;

          const newCanvas = document.createElement('canvas');
          newCanvas.width = value.width;
          newCanvas.height = value.height;
          const newCanvasContext = newCanvas.getContext('2d');
          newCanvasContext!.drawImage(
            value,
            0,
            0,
            newCanvas.width,
            newCanvas.height
          );

          return newCanvas;
        })
        .catch((error) =>
          Promise.reject(
            ApplicationError.FromError(
              error,
              'Failed to render page for display'
            )
          )
        );

      abortSignal?.throwIfAborted();

      await viewer.getAnnotationManager().drawAnnotations({
        pageNumber: pageNum,
        overrideCanvas: pageCanvas,
      });

      return {
        data: `url(${pageCanvas.toDataURL()})`,
        aspectRatio: pageCanvas.width / pageCanvas.height,
      };
    },
    [webViewerInstance]
  );

  const pdfdrawFactory = useCallback(
    () =>
      webViewerInstance?.Core.PDFNet.PDFDraw.create(RASTERIZE_RESOLUTION) ??
      Promise.resolve(undefined),
    [webViewerInstance]
  );
  const pdfdraw = useAsyncCreatedResource(pdfdrawFactory, pdfDrawCleanup);

  return {
    setCurrentPageNumber,
    addDocument,
    resetDocument: resetViewer,
    webViewerInstance,
    viewerDocument,
    getAllPageData,
    renderPage,
    pdfdraw,
  };
}
